import {
  CurrencyFormatted,
  TooltipTrigger,
  TooltipContent,
  Typography,
  Skeleton,
  Tooltip,
  Button,
  Badge,
  Card,
} from '@ui/components';
import { useGetEmployeeProfiles, useCurrentUser } from '@shared/hooks';
import { useEmployeesProfile } from '@pages/employees-profile/hooks';
import { CompanyPolicyResponse } from '@interfaces/models';
import { HasPermission } from '@shared/components';
import { Info, Pay } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState, FC } from 'react';
import { cn } from '@ui/utils';

import { RemoveEmployeeProfileDialog } from './remove-employee-profile-dialog';
import { EditEmployeesProfileDrawer } from '../edit-employees-profile-drawer';

interface EmployeesProfileMaxAmountCardProps {
  data?: CompanyPolicyResponse;
  isLoading?: boolean;
  className?: string;
}

export const EmployeesProfileMaxAmountCard: FC<EmployeesProfileMaxAmountCardProps> = ({
  className,
  isLoading,
  data,
}) => {
  const [isRemoveOpen, setIsRemoveOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { currency } = useCurrentUser();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { refetchEmployeeProfile } = useEmployeesProfile(data?.id);
  const { refetchEmployeeProfiles } = useGetEmployeeProfiles();

  return (
    <Card className={cn('p-4 flex items-center justify-between', className)}>
      {isLoading ? (
        <Skeleton className="h-full w-full" />
      ) : (
        <>
          <div className="flex items-center gap-4">
            <Badge variant="blue" size="large" icon>
              <Pay />
            </Badge>

            <Typography variant="h100">{t('EMPLOYEE_PROFILES.MAX_AMOUNT')}</Typography>

            <Typography className="text-weak">
              {data?.maxAmount ? (
                <CurrencyFormatted value={data.maxAmount} currency={currency} />
              ) : (
                t('EMPLOYEE_PROFILES.NO_LIMIT')
              )}
            </Typography>

            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="text-weak" />
              </TooltipTrigger>
              <TooltipContent alignOffset={50} side="bottom">
                {t('EMPLOYEE_PROFILES.MAX_AMOUNT_TOOLTIP')}
              </TooltipContent>
            </Tooltip>
          </div>
          <HasPermission permissionFeature="EMPLOYEES_PROFILE_EDIT_PROFILE" permissionType="READ">
            <Button onClick={() => setIsEditOpen(true)} className="min-w-1/3" variant="tertiary">
              {t('EMPLOYEE_PROFILES.EDIT')}
            </Button>
          </HasPermission>

          {data && (
            <>
              <EditEmployeesProfileDrawer
                onClose={({ handleSuccess }) => {
                  setIsEditOpen(false);
                  if (handleSuccess) {
                    refetchEmployeeProfiles();
                    refetchEmployeeProfile();
                  }
                }}
                onRemove={() => setIsRemoveOpen(true)}
                isOpen={isEditOpen}
                data={data}
              />

              <RemoveEmployeeProfileDialog
                onClose={({ handleSubmit }) => {
                  setIsRemoveOpen(false);
                  if (handleSubmit) {
                    refetchEmployeeProfiles();
                    refetchEmployeeProfile();
                    setIsEditOpen(false);
                    navigate(`./..`);
                  }
                }}
                isOpen={isRemoveOpen}
                id={data!.id}
              />
            </>
          )}
        </>
      )}
    </Card>
  );
};
